import { useEffect, useState } from "react";
import type { UIEvent } from "react";
import {
  ArrowDown,
  ArrowUp,
  Check,
  House,
  MagnifyingGlass,
  PencilSimple,
  Plus,
  Trash,
  X,
} from "@phosphor-icons/react";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";

import { getForYouItemImageUploadEndpoint } from "@/api";
import {
  homeContentQueryKeys,
  useHomeContent,
  useMedia,
  useMeditations,
} from "@/hooks";
import { ContentLayout } from "@/layout";
import { ROUTES } from "@/router";
import type { ForYouItem, Meditation } from "@/types";
import {
  Breadcrumb,
  Button,
  Heading,
  Input,
  Loader,
  SectionHeader,
} from "@/ui";
import { tw } from "@/utils";

interface ForYouForm {
  id?: string;
  eyebrow: string;
  title: string;
  link: string;
  isPublished: boolean;
  file?: File;
}

// The app never shows unpublished meditations, and the weekly list appears
// under "free", so the pickers only offer what the app can actually show.
const FEATURED_FILTERS = { published: "1" };
const WEEKLY_FILTERS = { free: "1", published: "1" };

interface PagedList {
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  fetchNextPage: () => unknown;
}

// Load the next page once a picker is scrolled close to the bottom.
const loadMoreOnScroll = (event: UIEvent<HTMLDivElement>, list: PagedList) => {
  const el = event.currentTarget;
  const nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
  if (nearBottom && list.hasNextPage && !list.isFetchingNextPage) {
    void list.fetchNextPage();
  }
};

const EMPTY_FORM: ForYouForm = {
  eyebrow: "",
  title: "",
  link: "",
  isPublished: true,
};

const getExtension = (file: File): string | undefined => {
  const ext = file.name.split(".").pop()?.toLowerCase();
  return ext === "jpg" || ext === "jpeg"
    ? "jpg"
    : ext === "png"
      ? "png"
      : undefined;
};

export const AppHome = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const {
    useHomeSettings,
    useForYouItems,
    useUpdateHomeSettings,
    useCreateForYouItem,
    useUpdateForYouItem,
    useDeleteForYouItem,
    useWeeklyMeditations,
    useLiveWeeklyMeditations,
    useUpdateWeeklyMeditations,
  } = useHomeContent();
  const { useInfiniteMeditations } = useMeditations();
  const { useUpload } = useMedia();

  const { data: settings, isLoading: loadingSettings } = useHomeSettings();
  const { data: forYou, isLoading: loadingForYou } = useForYouItems();
  const { data: weeklyData, isLoading: loadingWeekly } = useWeeklyMeditations();
  const { data: liveWeekly } = useLiveWeeklyMeditations();

  const updateSettings = useUpdateHomeSettings();
  const createItem = useCreateForYouItem();
  const updateItem = useUpdateForYouItem();
  const deleteItem = useDeleteForYouItem();
  const updateWeekly = useUpdateWeeklyMeditations();
  const { upload, isUploading } = useUpload();

  const [search, setSearch] = useState("");
  const [pickerOpen, setPickerOpen] = useState(false);
  const featuredList = useInfiniteMeditations(search, FEATURED_FILTERS);

  // ---- "This week's free meditations" editor state ----
  const [weekly, setWeekly] = useState<Meditation[]>([]);
  const [weeklySearch, setWeeklySearch] = useState("");
  const [weeklyPickerOpen, setWeeklyPickerOpen] = useState(false);
  const weeklyList = useInfiniteMeditations(weeklySearch, WEEKLY_FILTERS);

  useEffect(() => {
    if (weeklyData?.data) {
      setWeekly(weeklyData.data);
    }
  }, [weeklyData]);

  const addWeekly = (meditation: Meditation) => {
    setWeekly((current) =>
      current.some((m) => m.id === meditation.id)
        ? current
        : [...current, meditation],
    );
  };
  const removeWeekly = (id: string) =>
    setWeekly((current) => current.filter((m) => m.id !== id));
  const moveWeekly = (index: number, direction: -1 | 1) => {
    setWeekly((current) => {
      const target = index + direction;
      if (target < 0 || target >= current.length) {
        return current;
      }
      const next = [...current];
      const a = next[index];
      const b = next[target];
      if (!a || !b) {
        return current;
      }
      next[index] = b;
      next[target] = a;
      return next;
    });
  };
  const saveWeekly = async () => {
    await updateWeekly.mutateAsync(weekly.map((m) => m.id));
    void queryClient.invalidateQueries({
      queryKey: homeContentQueryKeys.useWeeklyMeditations(),
    });
  };

  const [form, setForm] = useState<ForYouForm | null>(null);

  const featured = settings?.data.featuredMeditation ?? null;
  const items = forYou?.data ?? [];

  const invalidateForYou = () =>
    queryClient.invalidateQueries({
      queryKey: homeContentQueryKeys.useForYouItems(),
    });
  const invalidateSettings = () =>
    queryClient.invalidateQueries({
      queryKey: homeContentQueryKeys.useHomeSettings(),
    });

  const setFeatured = async (meditationId: string | null) => {
    await updateSettings.mutateAsync(meditationId);
    await invalidateSettings();
    setSearch("");
  };

  const saveForm = async () => {
    if (!form?.title.trim() || !form.link.trim()) {
      return;
    }

    const extension = form.file ? getExtension(form.file) : undefined;
    const payload = {
      eyebrow: form.eyebrow.trim() || null,
      title: form.title.trim(),
      link: form.link.trim(),
      is_published: form.isPublished,
      image_extension: extension ?? null,
    };

    const saved = form.id
      ? (await updateItem.mutateAsync({ id: form.id, payload })).data
      : (await createItem.mutateAsync(payload)).data;

    if (form.file && extension) {
      await upload(form.file, getForYouItemImageUploadEndpoint(saved.id));
    }

    await invalidateForYou();
    setForm(null);
  };

  const removeItem = async (item: ForYouItem) => {
    if (!window.confirm(`Delete "${item.title}"?`)) {
      return;
    }
    await deleteItem.mutateAsync(item.id);
    await invalidateForYou();
  };

  const isSaving = createItem.isPending || updateItem.isPending || isUploading;

  return (
    <ContentLayout className="flex flex-col gap-y-6">
      <Breadcrumb
        items={[{ label: "App Home", href: ROUTES.appHome, icon: <House /> }]}
        current="App Home"
        onItemClick={(item) => {
          if (item.href) {
            navigate(item.href);
          }
        }}
      />

      <SectionHeader
        left={<Heading level={2} heading="App Home" className="text-3xl" />}
      />

      {/* ---------- Featured meditation ---------- */}
      <section className="rounded-xl border border-neutral-200 bg-white p-6 shadow-sm">
        <div className="mb-4 flex items-start justify-between gap-3">
          <div>
            <h3 className="text-xl font-bold text-primary-950">
              Featured meditation
            </h3>
            <p className="text-sm text-neutral-600">
              The hero card at the top of the app&apos;s home screen.
            </p>
          </div>
          {featured ? (
            <Button
              variant="outlined"
              size="sm"
              onClick={() => setFeatured(null)}
            >
              Remove
            </Button>
          ) : null}
        </div>

        {loadingSettings ? (
          <Loader />
        ) : featured ? (
          <div>
            <p className="mb-2 text-xs font-medium uppercase tracking-wide text-neutral-400">
              Preview — how it appears on the app home
            </p>
            <div className="max-w-sm overflow-hidden rounded-2xl border border-neutral-200 bg-white shadow-sm">
              <div className="relative h-32 w-full bg-neutral-200">
                {featured.image ? (
                  <img
                    src={featured.image}
                    alt={featured.name}
                    className="h-full w-full object-cover"
                  />
                ) : null}
                <span className="absolute left-3 top-3 rounded-md bg-secondary-500 px-2 py-1 text-[10px] font-bold uppercase tracking-wide text-tertiary-950">
                  Featured
                </span>
              </div>
              <div className="p-4">
                <p className="font-bold text-primary-950">{featured.name}</p>
                <p className="mt-1 text-sm font-semibold text-primary-700">
                  ▸ Start now
                </p>
              </div>
            </div>
          </div>
        ) : (
          <p className="rounded-lg bg-neutral-50 p-4 text-sm text-neutral-600">
            No featured meditation selected — the app will automatically show a
            featured or free meditation instead.
          </p>
        )}

        <div className="relative mt-4">
          <Input
            id="featured-search"
            label="Change featured meditation"
            placeholder="Search or browse meditations…"
            left={<MagnifyingGlass size={18} className="text-neutral-400" />}
            value={search}
            onFocus={() => setPickerOpen(true)}
            onBlur={() => window.setTimeout(() => setPickerOpen(false), 150)}
            onChange={(e) => setSearch(e.target.value)}
          />
          {pickerOpen ? (
            <div
              className="absolute z-20 mt-2 max-h-80 w-full divide-y divide-neutral-100 overflow-y-auto rounded-lg border border-neutral-200 bg-white shadow-lg"
              onScroll={(e) => loadMoreOnScroll(e, featuredList)}
            >
              {featuredList.isLoading ? (
                <div className="p-4">
                  <Loader />
                </div>
              ) : featuredList.meditations.length ? (
                <>
                  <p className="sticky top-0 bg-neutral-50 px-3 py-2 text-xs font-semibold text-neutral-500">
                    {featuredList.total ?? featuredList.meditations.length}{" "}
                    published meditations
                  </p>
                  {featuredList.meditations.map((meditation) => (
                    <button
                      key={meditation.id}
                      type="button"
                      onMouseDown={() => setFeatured(meditation.id)}
                      className={tw(
                        "flex w-full items-center gap-3 p-3 text-left transition hover:bg-neutral-50",
                        meditation.id === featured?.id && "bg-primary-50",
                      )}
                    >
                      {meditation.image ? (
                        <img
                          src={meditation.image}
                          alt={meditation.name}
                          className="h-10 w-10 rounded-md object-cover"
                        />
                      ) : (
                        <div className="h-10 w-10 rounded-md bg-neutral-200" />
                      )}
                      <span className="flex-1 text-sm text-primary-950">
                        {meditation.name}
                      </span>
                      {meditation.id === featured?.id ? (
                        <Check size={18} className="text-primary-700" />
                      ) : null}
                    </button>
                  ))}
                  {featuredList.isFetchingNextPage ? (
                    <div className="p-3">
                      <Loader />
                    </div>
                  ) : null}
                </>
              ) : (
                <p className="p-4 text-sm text-neutral-600">
                  No meditations found.
                </p>
              )}
            </div>
          ) : null}
        </div>
      </section>

      {/* ---------- This week's free meditations ---------- */}
      <section className="rounded-xl border border-neutral-200 bg-white p-6 shadow-sm">
        <div className="mb-4">
          <h3 className="text-xl font-bold text-primary-950">
            This week&apos;s free meditations
          </h3>
          <p className="text-sm text-neutral-600">
            A curated set shown on the app home. Reorder with the arrows. If
            left empty, the app automatically shows the newest free meditations.
          </p>
        </div>

        {loadingWeekly ? (
          <Loader />
        ) : weekly.length === 0 ? (
          <div className="rounded-lg bg-neutral-50 p-4 text-sm text-neutral-600">
            <p>
              None selected — the app automatically shows the newest free
              meditations.
            </p>
            {liveWeekly?.data.length ? (
              <>
                <p className="mt-3 font-semibold text-neutral-700">
                  Currently showing in the app:
                </p>
                <ul className="mt-2 flex flex-col gap-1">
                  {liveWeekly.data.map((m) => (
                    <li key={m.id} className="text-primary-950">
                      {m.name}
                    </li>
                  ))}
                </ul>
              </>
            ) : null}
          </div>
        ) : (
          <div className="flex flex-col divide-y divide-neutral-100 rounded-lg border border-neutral-200">
            {weekly.map((m, index) => (
              <div key={m.id} className="flex items-center gap-3 p-3">
                {m.image ? (
                  <img
                    src={m.image}
                    alt={m.name}
                    className="h-10 w-10 rounded-md object-cover"
                  />
                ) : (
                  <div className="h-10 w-10 rounded-md bg-neutral-200" />
                )}
                <span className="flex-1 text-sm text-primary-950">
                  {m.name}
                </span>
                <button
                  type="button"
                  onClick={() => moveWeekly(index, -1)}
                  disabled={index === 0}
                  aria-label="Move up"
                  className="p-1 text-neutral-400 hover:text-primary-700 disabled:opacity-30"
                >
                  <ArrowUp size={16} />
                </button>
                <button
                  type="button"
                  onClick={() => moveWeekly(index, 1)}
                  disabled={index === weekly.length - 1}
                  aria-label="Move down"
                  className="p-1 text-neutral-400 hover:text-primary-700 disabled:opacity-30"
                >
                  <ArrowDown size={16} />
                </button>
                <button
                  type="button"
                  onClick={() => removeWeekly(m.id)}
                  aria-label="Remove"
                  className="p-1 text-neutral-400 hover:text-error-700"
                >
                  <X size={16} />
                </button>
              </div>
            ))}
          </div>
        )}

        <div className="relative mt-4">
          <Input
            id="weekly-search"
            label="Add a meditation"
            placeholder="Search or browse free meditations…"
            left={<MagnifyingGlass size={18} className="text-neutral-400" />}
            value={weeklySearch}
            onFocus={() => setWeeklyPickerOpen(true)}
            onBlur={() =>
              window.setTimeout(() => setWeeklyPickerOpen(false), 150)
            }
            onChange={(e) => setWeeklySearch(e.target.value)}
          />
          {weeklyPickerOpen ? (
            <div
              className="absolute z-20 mt-2 max-h-80 w-full divide-y divide-neutral-100 overflow-y-auto rounded-lg border border-neutral-200 bg-white shadow-lg"
              onScroll={(e) => loadMoreOnScroll(e, weeklyList)}
            >
              {weeklyList.isLoading ? (
                <div className="p-4">
                  <Loader />
                </div>
              ) : weeklyList.meditations.length ? (
                <>
                  <p className="sticky top-0 bg-neutral-50 px-3 py-2 text-xs font-semibold text-neutral-500">
                    {weeklyList.total ?? weeklyList.meditations.length} free
                    meditations
                  </p>
                  {weeklyList.meditations.map((meditation) => {
                    const alreadyAdded = weekly.some(
                      (m) => m.id === meditation.id,
                    );
                    return (
                      <button
                        key={meditation.id}
                        type="button"
                        onMouseDown={() => addWeekly(meditation)}
                        disabled={alreadyAdded}
                        className={tw(
                          "flex w-full items-center gap-3 p-3 text-left transition hover:bg-neutral-50 disabled:opacity-40",
                          alreadyAdded && "bg-primary-50",
                        )}
                      >
                        {meditation.image ? (
                          <img
                            src={meditation.image}
                            alt={meditation.name}
                            className="h-10 w-10 rounded-md object-cover"
                          />
                        ) : (
                          <div className="h-10 w-10 rounded-md bg-neutral-200" />
                        )}
                        <span className="flex-1 text-sm text-primary-950">
                          {meditation.name}
                        </span>
                        {alreadyAdded ? (
                          <Check size={18} className="text-primary-700" />
                        ) : (
                          <Plus size={18} className="text-neutral-400" />
                        )}
                      </button>
                    );
                  })}
                  {weeklyList.isFetchingNextPage ? (
                    <div className="p-3">
                      <Loader />
                    </div>
                  ) : null}
                </>
              ) : (
                <p className="p-4 text-sm text-neutral-600">
                  No meditations found.
                </p>
              )}
            </div>
          ) : null}
        </div>

        <div className="mt-4 flex justify-end">
          <Button
            size="sm"
            onClick={() => void saveWeekly()}
            disabled={updateWeekly.isPending}
          >
            {updateWeekly.isPending ? "Saving…" : "Save weekly meditations"}
          </Button>
        </div>
      </section>

      {/* ---------- For You boxes ---------- */}
      <section className="rounded-xl border border-neutral-200 bg-white p-6 shadow-sm">
        <div className="mb-4 flex items-start justify-between gap-3">
          <div>
            <h3 className="text-xl font-bold text-primary-950">
              &ldquo;For You&rdquo; boxes
            </h3>
            <p className="text-sm text-neutral-600">
              The course &amp; CPD cards on the app home. Lower position numbers
              show first.
            </p>
          </div>
          {form === null ? (
            <Button size="sm" onClick={() => setForm({ ...EMPTY_FORM })}>
              <Plus size={16} /> Add box
            </Button>
          ) : null}
        </div>

        {form ? (
          <div className="mb-4 space-y-4 rounded-lg border border-neutral-200 bg-neutral-50 p-5">
            <Input
              id="fy-eyebrow"
              label="Eyebrow (optional)"
              placeholder="e.g. Courses & CPD"
              value={form.eyebrow}
              onChange={(e) => setForm({ ...form, eyebrow: e.target.value })}
            />
            <Input
              id="fy-title"
              label="Title"
              placeholder="e.g. Deepening your practice"
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
            />
            <Input
              id="fy-link"
              label="Link (URL)"
              placeholder="https://…"
              value={form.link}
              onChange={(e) => setForm({ ...form, link: e.target.value })}
            />
            <div className="space-y-1">
              <label
                htmlFor="fy-image"
                className="block text-sm font-medium text-primary-950"
              >
                Image {form.id ? "(leave empty to keep current)" : ""}
              </label>
              <input
                id="fy-image"
                type="file"
                accept="image/png,image/jpeg"
                onChange={(e) =>
                  setForm({ ...form, file: e.target.files?.[0] })
                }
                className="block w-full text-sm text-neutral-600 file:mr-3 file:rounded-full file:border-0 file:bg-primary-950 file:px-4 file:py-2 file:text-sm file:font-medium file:text-primary-50 hover:file:bg-primary-700"
              />
            </div>
            <label
              htmlFor="fy-published"
              className="flex items-center gap-2 text-sm text-primary-950"
            >
              <input
                id="fy-published"
                type="checkbox"
                checked={form.isPublished}
                onChange={() =>
                  setForm({ ...form, isPublished: !form.isPublished })
                }
                className="h-4 w-4 rounded border-neutral-300 text-primary-950"
              />
              Published
            </label>
            <div className="flex gap-3">
              <Button size="sm" onClick={saveForm} disabled={isSaving}>
                {isSaving ? "Saving…" : "Save box"}
              </Button>
              <Button
                size="sm"
                variant="outlined"
                onClick={() => setForm(null)}
                disabled={isSaving}
              >
                Cancel
              </Button>
            </div>
          </div>
        ) : null}

        {loadingForYou ? (
          <Loader />
        ) : items.length ? (
          <div className="space-y-2">
            {items.map((item) => (
              <div
                key={item.id}
                className="flex items-center gap-4 rounded-lg bg-neutral-50 p-4 transition hover:bg-neutral-100"
              >
                {item.image ? (
                  <img
                    src={item.image}
                    alt={item.title}
                    className="h-12 w-12 rounded-md object-cover"
                  />
                ) : (
                  <div className="h-12 w-12 rounded-md bg-neutral-200" />
                )}
                <div className="min-w-0 flex-1">
                  {item.eyebrow ? (
                    <p className="text-xs font-semibold uppercase tracking-wide text-primary-700">
                      {item.eyebrow}
                    </p>
                  ) : null}
                  <p className="truncate font-medium text-primary-950">
                    {item.title}
                  </p>
                  <a
                    href={item.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="truncate text-xs text-tertiary-600 hover:underline"
                  >
                    {item.link}
                  </a>
                </div>
                {!item.isPublished ? (
                  <span className="rounded-full bg-neutral-200 px-2 py-0.5 text-xs font-medium text-neutral-700">
                    Draft
                  </span>
                ) : null}
                <div className="flex gap-2">
                  <Button
                    variant="outlined"
                    size="sm"
                    onClick={() =>
                      setForm({
                        id: item.id,
                        eyebrow: item.eyebrow ?? "",
                        title: item.title,
                        link: item.link,
                        isPublished: item.isPublished,
                      })
                    }
                  >
                    <PencilSimple size={16} />
                  </Button>
                  <Button
                    variant="outlined"
                    size="sm"
                    onClick={() => removeItem(item)}
                  >
                    <Trash size={16} />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="py-6 text-center text-neutral-600">
            No boxes yet. Click &ldquo;Add box&rdquo; to create one.
          </p>
        )}
      </section>
    </ContentLayout>
  );
};
